import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { signInWithCustomToken } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions'; // <--- NEW IMPORT
import { auth, functions } from '../firebase/config'; // <--- NEW IMPORT
import { Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { markStaffPreviewBrowser } from '../utils/portalPreview'; // qview-6b2d4e91
import { safePortalRedirect } from '../utils/portalRedirect'; // portalredir-2e9b6d38

const PortalLogin = () => {
    const { token: paramToken } = useParams();
    const [searchParams] = useSearchParams();
    const queryToken = searchParams.get('token');
    const redirectPath = searchParams.get('redirect');
    const sessionEnded = !!searchParams.get('ended'); // x7portal-6e1a3d58
    
    // The "token" in the URL is now actually a "portalKey" (UUID)
    const portalKey = paramToken || queryToken;

    const navigate = useNavigate();
    const [status, setStatus] = useState('loading'); 
    const [errorMessage, setErrorMessage] = useState('');
    
    const attemptMade = useRef(false);

    useEffect(() => {
        if (!portalKey) {
            setStatus('error');
            // x7portal-6e1a3d58 — X7: the portal signs a customer out when their 30-day sign-in ends.
            setErrorMessage(sessionEnded
                ? 'Your sign-in has expired. Open the latest link the company sent you, or ask them for a new one.'
                : 'Invalid link. Please use the secure link sent to your email.');
            return;
        }

        if (attemptMade.current) return;
        attemptMade.current = true;

        const loginUser = async () => {
            try {
                // qview-6b2d4e91 — Seam 10 staff-preview check, BEFORE the
                // exchange replaces whoever is signed in. If a STAFF member
                // (any signed-in user without the portalAccess claim) is already
                // signed in on this browser, this is a staff preview: mark the
                // browser so its portal pages never report estimate views.
                // authStateReady() waits for the saved session to load —
                // without it currentUser is still null on a fresh page. Never
                // blocks the login: any failure here just skips the mark.
                try {
                    await auth.authStateReady();
                    const prior = auth.currentUser;
                    if (prior) {
                        const claims = (await prior.getIdTokenResult()).claims || {};
                        if (!claims.portalAccess) markStaffPreviewBrowser();
                    }
                } catch (previewErr) {
                    console.warn('PortalLogin: staff-preview check skipped', previewErr);
                }

                // 1. EXCHANGE THE KEY FOR A REAL TOKEN
                const exchangeFn = httpsCallable(functions, 'exchangePortalToken');
                const result = await exchangeFn({ portalKey: portalKey });
                const realAuthToken = result.data.token;

                // 2. SIGN IN
                await signInWithCustomToken(auth, realAuthToken);
                
                setStatus('success');
                
                // portalredir-2e9b6d38 — the redirect is validated (in-portal paths only; anything
                // else → portal home), and `replace: true` swaps THIS entry — the URL carrying the
                // login key — out of browser history, so Back can't return to it (founder
                // decision 3, security audit 2026-10-02).
                setTimeout(() => {
                    navigate(safePortalRedirect(redirectPath), { replace: true });
                }, 1500);

            } catch (error) {
                console.error("Portal Login Error:", error);
                setStatus('error');
                
                if (error.message && error.message.includes('expired')) {
                    setErrorMessage('This login link has expired. Please request a new one.');
                } else {
                    setErrorMessage('We could not log you in. The link may be invalid or expired.');
                }
            }
        };

        loginUser();
    }, [portalKey, navigate, redirectPath, sessionEnded]);

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
            <div className="bg-white max-w-md w-full rounded-lg shadow-md p-8 text-center">
                <div className="mb-8">
                    <h1 className="text-2xl font-bold text-gray-800 tracking-tight">
                        CLIENT<span className="text-green-600">HUB</span>
                    </h1>
                </div>

                {status === 'loading' && (
                    <div className="flex flex-col items-center py-4">
                        <Loader2 className="h-10 w-10 text-blue-600 animate-spin mb-4" />
                        <h2 className="text-lg font-semibold text-gray-700">Verifying Link...</h2>
                        <p className="text-gray-500 text-sm mt-2">Checking security credentials</p>
                    </div>
                )}

                {status === 'success' && (
                    <div className="flex flex-col items-center py-4">
                        <div className="h-12 w-12 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-4">
                            <CheckCircle size={28} />
                        </div>
                        <h2 className="text-lg font-semibold text-gray-800">Login Successful!</h2>
                        <p className="text-gray-500 text-sm mt-2">Opening your secure document...</p>
                    </div>
                )}

                {status === 'error' && (
                    <div className="flex flex-col items-center py-4">
                        <div className="h-12 w-12 bg-red-100 text-red-600 rounded-full flex items-center justify-center mb-4">
                            <AlertCircle size={28} />
                        </div>
                        <h2 className="text-lg font-semibold text-gray-800">Login Failed</h2>
                        <p className="text-red-600 text-sm mt-2 bg-red-50 p-3 rounded-md w-full">
                            {errorMessage}
                        </p>
                        <button 
                            onClick={() => window.location.reload()}
                            className="mt-6 px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition-colors text-sm font-medium"
                        >
                            Try Again
                        </button>
                    </div>
                )}
            </div>
            <div className="mt-8 text-center text-gray-400 text-xs">
                <p>&copy; {new Date().getFullYear()} Secure Customer Portal</p>
            </div>
        </div>
    );
};

export default PortalLogin;